import React, { useEffect, useRef } from "react";
import { useTheme } from "@mui/material/styles";
import * as echarts from "echarts";


// // 樣式表
// const iconStyleSheet = {
//     green: square_green_icon,
//     blue: square_blue_icon
// }

const lineStyleSheet = (theme) => ({
  green: {
    lineStyle: { color: theme.palette.primary.greenPrimary, width: 2.2 },
    itemStyle: { color: theme.palette.primary.greenPrimary },
    areaStyle: {
      color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
        { offset: 0, color: theme.palette.primary.greenGradientPrimary },
        { offset: 0.5, color: theme.palette.primary.greenGradientSecound },
        { offset: 0.8, color: theme.palette.primary.greenGradientThird },
        { offset: 1, color: theme.palette.primary.rgbTransparent },
      ])
    }
  },
  orange: {
    lineStyle: { color: theme.palette.primary.orangeThird, width: 2.2 },
    itemStyle: { color: theme.palette.primary.orangeThird },
    areaStyle: {
      color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
        { offset: 0, color: theme.palette.primary.orangeGradientPrimary },
        { offset: 0.5, color: theme.palette.primary.orangeGradientSecound },
        { offset: 0.8, color: theme.palette.primary.orangeGradientThird },
        { offset: 1, color: theme.palette.primary.rgbTransparent },
      ])
    }
  },
  red: {
    lineStyle: { color: theme.palette.primary.redPrimary, width: 2.2 },
    itemStyle: { color: theme.palette.primary.redPrimary },
    areaStyle: {
      color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
        { offset: 0, color: theme.palette.primary.redGradientPrimary },
        { offset: 0.5, color: theme.palette.primary.redGradientSecound },
        { offset: 0.8, color: theme.palette.primary.redGradientThird },
        { offset: 1, color: theme.palette.primary.rgbTransparent },
      ])
    }
  },
});

const StatTrendLineChart = ({ lineChartData = [], height = 100, lineColor }) => {
  const theme = useTheme();

  const styleSheet = lineStyleSheet(theme);

  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    const chartDom = chartRef.current;
    if (!chartDom) return;

    // 初始化 ECharts
    const chart = echarts.init(chartDom, null, {
      renderer: 'svg',
      useDirtyRect: true,
    });
    chartInstance.current = chart;

    const sortedData = [...lineChartData].sort(
      (a, b) => a.timestamp - b.timestamp
    );

    const option = {
      grid: {
        left: "3%",
        right: "3%",
        top: "5%",
        bottom: 0,
        containLabel: false,
      },
      xAxis: {
        type: "time",
        show: false,
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { show: false },
      },
      yAxis: {
        type: "value",
        show: false,
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { show: false },
      },
      tooltip: {
        trigger: "axis",
        backgroundColor: theme.palette.primary.greyPrimaryTransparency,
        textStyle: { color: theme.palette.primary.white },
        extraCssText: `border-radius: 5px; max-width: 300px;`,
        formatter: (params) => {
          if (!params || params.length === 0) return "";

          const currentTime = new Date(params[0].value[0]);
          currentTime.setDate(currentTime.getDate() - 1); // Reduce 1 day.

          const formatDate = (date) => {
            const y = date.getFullYear();
            const m = (date.getMonth() + 1).toString().padStart(2, "0");
            const d = date.getDate().toString().padStart(2, "0");

            return `${y}-${m}-${d}`;
          };

          let tooltipText = `<div style="font-size: 12px;">`;

          params.forEach(param => {
            tooltipText += `
              <div style="display:flex; align-items:center; gap:6px; color:#FFFFFF;">
                <span>${formatDate(currentTime)}</span>
                <span>&nbsp;&nbsp;</span>
                <span style="font-weight:bold;">${param.value[1]} %</span>
              </div>
            `;
          });

          return tooltipText;
        },
      },
      series: [
        {
          type: "line",
          data: sortedData.map((i) => [
            i.timestamp * 1000,
            i.usage,
          ]),
          showSymbol: true,
          symbol: "circle",
          symbolSize: (values, data) => {
            const { dataIndex } = data;
            const hasPrev = sortedData?.[dataIndex - 1]?.usage !== null;
            const hasNext = sortedData?.[dataIndex + 1]?.usage !== null;

            return (hasPrev || hasNext) ? 0 : 4;
          },
          connectNulls: false,
          lineStyle: styleSheet[lineColor].lineStyle,
          itemStyle: styleSheet[lineColor].itemStyle,
          areaStyle: styleSheet[lineColor].areaStyle,
        },
      ],
    };

    chartInstance.current.setOption(option, {
      notMerge: true,
    });
  }, [lineChartData]);

  return (
    <div
      ref={chartRef}
      style={{
        width: "100%",
        height,
        background: theme.palette.primary.white,
      }}
    />
  );
};

export default StatTrendLineChart;